// src/pages/conversaciones/components/ChatInput.tsx
import { useState } from 'react';
import { Button, Textarea } from '@/components/ui';
import { Send, Lock, Paperclip } from 'lucide-react';
import { useEnviarMensaje } from '@/hooks/queries/useConversaciones';
import { cn } from '@/lib/utils';

interface Props {
  conversacionId: string;
  disabled?: boolean;
}

export function ChatInput({ conversacionId, disabled }: Props) {
  const [contenido, setContenido] = useState('');
  const [esInterno, setEsInterno] = useState(false);
  const enviarMutation = useEnviarMensaje();

  const handleEnviar = async () => {
    if (!contenido.trim()) return;

    try {
      await enviarMutation.mutateAsync({
        conversacionId,
        data: { contenido, es_interno: esInterno },
      });
      setContenido('');
    } catch {
      // Error manejado por el hook
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  };

  if (disabled) {
    return (
      <div className="p-4 border-t bg-gray-100 text-center text-gray-500">
        Esta conversacion esta cerrada
      </div>
    );
  }

  return (
    <div className="p-4 border-t">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => setEsInterno(!esInterno)}
          className={cn(
            'flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors',
            esInterno
              ? 'bg-orange-100 text-orange-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          )}
        >
          <Lock className="h-3 w-3" />
          {esInterno ? 'Nota Interna' : 'Mensaje Publico'}
        </button>
      </div>

      <div className="flex gap-2">
        <Textarea
          value={contenido}
          onChange={(e) => setContenido(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={esInterno ? 'Escriba una nota interna...' : 'Escriba su mensaje...'}
          className={cn(
            'flex-1 min-h-[60px] max-h-[120px] resize-none',
            esInterno && 'border-orange-300 focus:ring-orange-500'
          )}
          rows={2}
        />
        <div className="flex flex-col gap-2">
          <Button
            onClick={handleEnviar}
            disabled={!contenido.trim() || enviarMutation.isPending}
            loading={enviarMutation.isPending}
          >
            <Send className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" disabled>
            <Paperclip className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
